<template>
  <div v-if="isVisible" class="modal-overlay" @click="closeModal">
    <div class="modal-container" @click.stop>
      <!-- Title Bar -->
      <div class="title-bar">
        <h2 class="modal-title">Notificación de Pago Vencido</h2>
        <button class="close-button" @click="closeModal">
          <img src="@/assets/close-icon.svg" alt="close" />
        </button>
      </div>
      <!-- Form Content -->
      <div class="form-content">
        <!-- Billing Month Selection (only for new register) -->
        <div class="input-field">
          <label class="input-title">Mes de facturación vencido*</label>
          <CustomSelect
            id="period"
            v-model="selectedPeriodLocal"
            :options="historyData"
            option-value="id"
            option-label="billingMonth"
            placeholder="Seleccionar un período..."
          />
          <span v-if="!selectedPeriodLocal" class="required-text"
            >Es necesario seleccionar un período</span
          >
        </div>
        <div class="input-field">
          <label class="input-title">Tipo de notificación*</label>
          <CustomSelect
            id="notification-type"
            v-model="selectedNotificationTypeLocal"
            :options="notificationTypes"
            option-value="value"
            option-label="value"
            placeholder="Seleccionar un tipo..."
          />
          <span v-if="!selectedNotificationTypeLocal" class="required-text"
            >Es necesario seleccionar un tipo</span
          >
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="button-container">
        <CustomButton text="Cancelar" variant="cancel" :show-icon="false" @click="closeModal" />
        <CustomButton
          text="Notificación de Muestra"
          variant="primary"
          :show-icon="false"
          :disabled="!isFormValid || isLoading"
          @click="() => handleClick(true)"
        />
        <CustomButton
          text="Enviar Notificación"
          variant="secondary"
          :show-icon="false"
          :disabled="!isFormValid || isLoading"
          @click="() => handleClick(false)"
        />
      </div>
    </div>
  </div>
  <ToastMessage
    v-model="showToast"
    :message="toastMessage"
    :submessage="toastSubmessage"
    :duration="10000"
    :type="toastType"
    :icon="toastIcon"
  />
</template>

<script setup lang="ts">
import CustomSelect from './CustomSelect.vue'
import CustomButton from './CustomButton.vue'
import { computed, inject, onMounted, ref, watch } from 'vue'
import type { ApiService } from '@/services/apiService'
import type { NotificationPresetDTO, StatusAccountPeriodDto } from 'commons'
import { formatMonthYear } from '@/utils/dateParsing'
import ToastMessage from './ToastMessage.vue'
import errorIcon from '@/assets/error-icon-white.svg'
import checkIcon from '@/assets/check-icon.svg'

const apiService = inject<ApiService>('apiService')
const historyData = ref<{ id: string; billingMonth: string }[]>([])

const notificationTypes = ref<string[]>([])

const showToast = ref(false)
const toastMessage = ref('')
const toastSubmessage = ref('')
const toastType = ref<'success' | 'error'>('success')
const toastIcon = ref<string | undefined>(undefined)

if (!apiService) {
  throw new Error('apiService is not provided')
}

const props = defineProps<{
  businessId: string
  isVisible: boolean
  selectedPeriod: string | undefined
  selectedNotificationType: string | undefined
}>()

const selectedPeriodLocal = ref<string | undefined>(props.selectedPeriod)
const selectedNotificationTypeLocal = ref<string | undefined>(props.selectedNotificationType)
const emit = defineEmits<{
  close: []
}>()

let notificationPreset: NotificationPresetDTO | null = null

onMounted(async () => {
  const accountPeriods: StatusAccountPeriodDto[] =
    await apiService.getStatusAccountPeriodsLightByBusinessId(props.businessId)
  historyData.value = accountPeriods.map((period) => ({
    id: period.id,
    billingMonth: formatMonthYear(period.month, period.year.toString()).toUpperCase(),
  }))
  notificationTypes.value = await apiService.getNotificationTypes()
  notificationPreset = await apiService.getNotificationPresetByBusinessId(props.businessId)
  if (notificationPreset) {
    selectedPeriodLocal.value = notificationPreset.periodId
    selectedNotificationTypeLocal.value = notificationPreset.notificationType
  }
})

const closeModal = () => {
  emit('close')
}

const isFormValid = computed(() => {
  return selectedPeriodLocal.value && selectedNotificationTypeLocal.value
})

// Watch for modal open and reset local values from props
watch(
  () => props.isVisible,
  (newVal) => {
    if (newVal) {
      // Si las props traen valor, usarlo. Si no, usar el preset si existe.
      selectedPeriodLocal.value =
        props.selectedPeriod || (notificationPreset && notificationPreset.periodId) || undefined
      selectedNotificationTypeLocal.value =
        props.selectedNotificationType ||
        (notificationPreset && notificationPreset.notificationType) ||
        undefined
    }
  },
)

const isLoading = ref(false)

const handleClick = async (isEmailSample: boolean) => {
  if (!isFormValid.value) return
  isLoading.value = true
  try {
    await apiService.sendDuePaymentNotification(
      selectedPeriodLocal.value!,
      selectedNotificationTypeLocal.value!,
      isEmailSample,
    )
    toastMessage.value = isEmailSample ? 'Notificación de Muestra' : 'Notificación Enviada'
    toastSubmessage.value = isEmailSample
      ? 'Se envió la notificación de muestra exitosamente'
      : 'Se envió la notificación exitosamente'
    toastType.value = 'success'
    toastIcon.value = checkIcon
    showToast.value = true
  } catch (error) {
    console.error('Error sending notification:', error)
    toastMessage.value = 'Error'
    toastSubmessage.value = 'Error al enviar la notificación'
    toastType.value = 'error'
    toastIcon.value = errorIcon
    showToast.value = true
  } finally {
    isLoading.value = false
  }
}
</script>

<style scoped>
/* Modal Overlay */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

/* Modal Container */
.modal-container {
  width: 863px;
  max-height: 90vh;
  background: #ffffff;
  border-radius: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0px 0px 16px;
  gap: 8px;
  position: relative;
  overflow: hidden;
  z-index: 10000;
}

/* Title Bar */
.title-bar {
  width: 863px;
  height: 59px;
  background: #ffffff;
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.15);
  border-radius: 0px 0px 16px 16px;
  flex: none;
  order: 0;
  align-self: stretch;
  flex-grow: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 32px;
}

.modal-title {
  width: 645px;
  height: 31px;
  font-family: 'Montserrat', sans-serif;
  font-style: normal;
  font-weight: 500;
  font-size: 24px;
  line-height: 130%;
  color: #4f4f4f;
  margin: 0;
  text-align: left;
}

.close-button {
  width: 32px;
  height: 32px;
  border-radius: 50px;
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-button img {
  width: 20px;
  height: 20px;
}

/* Form Content */
.form-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 20px 40px;
  gap: 16px;
  isolation: isolate;
  width: 100%;
  flex: 1;
  order: 1;
  align-self: stretch;
  flex-grow: 1;
  z-index: 1;
  overflow-y: auto;
  overflow-x: hidden;
  max-height: calc(90vh - 120px);
}

/* Input Field */
.input-field {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px;
  gap: 1px;
  isolation: isolate;
  width: 100%;
  min-height: 80px;
  flex: none;
  order: 0;
  align-self: stretch;
  flex-grow: 0;
  z-index: 1;
}

.input-title {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: 0px;
  gap: 8px;
  width: 100%;
  height: 22px;
  flex: none;
  order: 0;
  align-self: stretch;
  flex-grow: 0;
  z-index: 1;
  font-family: 'Montserrat', sans-serif;
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 140%;
  color: #2b2a2a;
}

.input-text::placeholder {
  color: #a1a1a1;
}

/* Button Container */
.button-container {
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  padding: 0px 40px;
  gap: 20px;
  width: 863px;
  height: 48px;
  flex: none;
  order: 2;
  align-self: stretch;
  flex-grow: 0;
  z-index: 0;
  flex-shrink: 0;
}
</style>
